import React, { useMemo } from "react";
import {
  Settings,
  Search,
  SquarePen,
  ArrowLeft,
  X,
  Maximize2,
  Share2,
  Languages,
  Facebook,
  Instagram,
  Linkedin,
  Music2,
  UserPlus,
  UserMinus,
  SunMoon,
  HardDriveDownload,
  ShieldCheck,
  UserRoundCheck,
  UserRoundX,
  Inbox,
  ChevronDown,
  Download,
  Activity,
  Wrench,
  PenLine,
  LogOut,
  Bell,
  BellOff,
  Twitter,
  Youtube,
  AtSign,
  Pin,
  Ghost,
  MessageCircle,
  Send,
  Layers,
  ChevronUp,
  Mail,
  MailOpen,
} from "lucide-react";
import { FOLDERS, PEC_ACCOUNTS, ACCOUNTS } from "../mock";

// Icona "Cartelle": vaschetta della posta con freccia che scende (come da mockup)
function IconaCartelle() {
  return (
    <svg className="icona-cartelle" viewBox="0 0 48 48" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="menu-icon-img">
      <path d="M8 17 L2 30 V44 H46 V30 L40 17" />
      <path d="M8 17 H18 M30 17 H40" />
      <path d="M2 30 H13 l1.5 4.2 a5 5 0 0 0 4.7 3.3 H28.8 a5 5 0 0 0 4.7 -3.3 L35 30 H46" />
      <path d="M33 4 H30 a6 6 0 0 0 -6 6 V27" />
      <path d="M17.5 20.5 L24 27 L30.5 20.5" />
    </svg>
  );
}

const SOCIALS = [
  { id: "Facebook", icon: Facebook, color: "#1877f2" },
  { id: "Instagram", icon: Instagram, color: "#e1306c" },
  { id: "TikTok", icon: Music2, color: "#010101" },
  { id: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  { id: "X", icon: Twitter, color: "#000000" },
  { id: "YouTube", icon: Youtube, color: "#ff0000" },
  { id: "Threads", icon: AtSign, color: "#000000" },
  { id: "Pinterest", icon: Pin, color: "#e60023" },
  { id: "Snapchat", icon: Ghost, color: "#d9b800" },
  { id: "WhatsApp", icon: MessageCircle, color: "#25d366" },
  { id: "Telegram", icon: Send, color: "#229ed9" },
];

export default function TopBar({
  mode,
  onBack,
  onFullscreen,
  onTranslate,
  onAccept,
  onReject,
  openMenu,
  setOpenMenu,
  folder,
  setFolder,
  accountFilter,
  setAccountFilter,
  categoria,
  setCategoria,
  socialFilter,
  setSocialFilter,
  searchOpen,
  setSearchOpen,
  query,
  setQuery,
  onCompose,
  dark,
  toggleDark,
  onResetDemo,
  accounts,
  pecAccounts,
  socialAccounts,
  onAddAccount,
  onSignatures,
  onDeleteAccountOpen,
  onLogout,
  title,
  isPec,
  notifiche,
  onToggleNotifiche,
  unread = {},
  backupAllowed,
  onBackup,
  isAdmin,
  onAdmin,
  soloNonLette,
  onToggleNonLette,
  socialInArrivo,
  onToggleSocialInArrivo,
  stato,
  onCheckStato,
  onInstall,
  installabile,
  onDiagnostica,
  onManutenzione,
  onRelay,
}) {
  const accountList = accounts || ACCOUNTS;
  const accTutti = useMemo(() => accountList.filter((a) => a.id === "tutti"), [accountList]);
  const accAltri = useMemo(() => accountList.filter((a) => a.id !== "tutti"), [accountList]);
  const Badge = ({ n, id }) =>
    n > 0 ? (
      <span className="unread-badge" data-testid={`badge-${id}`}>
        {n > 99 ? "99+" : n}
      </span>
    ) : null;
  const toggle = (id) => setOpenMenu(openMenu === id ? null : id);

  if (mode === "detail") {
    return (
      <div className="topbar" data-testid="topbar-detail">
        <button className="icon-btn icon-circle back-btn con-tip" onClick={onBack} data-testid="back-btn" aria-label="Indietro" data-tip="Indietro">
          <ArrowLeft size={28} strokeWidth={2.8} />
        </button>
        <h1 className={`topbar-title ${isPec ? "pec" : ""}`} title={title || ""} data-testid="detail-title">
          {title || ""}
        </h1>
        {onFullscreen && (
          <button
            className="icon-btn con-tip"
            onClick={onFullscreen}
            aria-label="Schermo intero"
            data-tip="Schermo intero"
            data-testid="detail-fullscreen-btn"
          >
            <Maximize2 size={21} strokeWidth={2.6} />
          </button>
        )}
        {onTranslate && (
          <button className="icon-btn translate-btn con-tip" onClick={onTranslate} aria-label="Traduci" data-tip="Traduci" data-testid="detail-translate-btn">
            <Languages size={26} strokeWidth={2.5} />
          </button>
        )}
        {onAccept && (
          <button
            className="icon-btn accept-btn con-tip"
            onClick={onAccept}
            aria-label="Accetta"
            data-tip="Accetta"
            data-testid="detail-accept-btn"
          >
            <UserRoundCheck size={24} strokeWidth={2.5} />
          </button>
        )}
        <button
          className="icon-btn reject-btn con-tip"
          onClick={onReject}
          aria-label="Rifiuta"
          data-tip="Rifiuta"
          data-testid="detail-reject-btn"
        >
          <UserRoundX size={24} strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  const pecList = pecAccounts || PEC_ACCOUNTS;
  const isPecSel = pecList.some((p) => p.id === accountFilter);
  const socialSel = (socialAccounts || []).find((a) => a.id === accountFilter);
  const casellaTipo = (() => {
    if (categoria === "social") return "sel-social";
    return isPecSel ? "sel-pec" : "sel-account";
  })();
  const casellaLabel = (() => {
    if (categoria === "social") return socialSel?.label || socialFilter || "SOCIAL";
    if (accountFilter === "tutti") return "TUTTI";
    return (accountList.find((a) => a.id === accountFilter) || pecList.find((a) => a.id === accountFilter))?.label || "TUTTI";
  })();
  const casellaUnread = (() => {
    if (categoria !== "social") return accountFilter === "tutti" ? unread.tutti : unread.byAccount?.[accountFilter];
    if (socialSel) return unread.byAccount?.[socialSel.id];
    if (socialFilter) return unread.bySocial?.[socialFilter];
    return unread.social;
  })();
  // Pallino stato connessione (verde ok, rosso errore, grigio non ancora controllato); tocco = ricontrolla
  const descriviStato = (s) => {
    if (!s || s.imap === null) return { cls: "grigio", title: "Stato non ancora controllato (tocca per verificare)" };
    if (s.imap && s.smtp !== false) return { cls: "verde", title: "Server di posta raggiungibile" };
    return { cls: "rosso", title: "Problema di connessione: " + (s.errore || "") };
  };
  const StatoDot = ({ id }) => {
    const s = stato?.[id];
    const { cls, title } = descriviStato(s);
    return (
      <span
        className={`stato-dot ${cls}`}
        title={title}
        role="button"
        onClick={(e) => { e.stopPropagation(); onCheckStato && onCheckStato(id); }}
        data-testid={`stato-${id}`}
      />
    );
  };

  return (
    <div className={`topbar ${openMenu ? "menu-aperto" : ""}`} data-testid="topbar-list">
      {/* Hamburger -> tendina cartelle */}
      <div className="anchor-wrap">
        <button
          className="icon-btn con-tip"
          onClick={() => toggle("folders")}
          data-testid="menu-btn"
          aria-label="Cartelle"
          data-tip="Cartelle"
          style={openMenu === "folders" ? { background: "var(--yellow-soft)" } : undefined}
        >
          <IconaCartelle />
        </button>
        {openMenu === "folders" && (
          <div className="dropdown align-left" data-title="Cartelle" data-testid="dropdown-folders">
            {FOLDERS.map((f) => (
              <button
                key={f.id}
                className={`folder-btn ${folder === f.id ? "active" : ""}`}
                onClick={() => {
                  setFolder(f.id);
                  setOpenMenu(null);
                }}
                data-testid={`folder-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {searchOpen ? (
        <>
          <input
            className="search-input"
            placeholder="Cerca email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            data-testid="search-input"
          />
          <button
            className="icon-btn"
            onClick={() => {
              setSearchOpen(false);
              setQuery("");
            }}
            aria-label="Chiudi ricerca"
            data-testid="search-close"
          >
            <X size={20} strokeWidth={2.6} />
          </button>
        </>
      ) : (
        <>
          {/* Un solo tasto "Caselle": mostra la casella corrente e apre il popup PEC / Social / Account */}
          <div className="tab-row" data-testid="category-tabs">
            <div className="anchor-wrap">
              <button
                className={`acc-tab tab-caselle con-tip ${casellaTipo}`}
                onClick={() => toggle("caselle")}
                data-testid="tab-caselle"
                data-tip="Scegli casella"
              >
                <Inbox size={20} strokeWidth={2.6} />
                <span className="tab-caselle-label">{casellaLabel}</span>
                <ChevronDown size={16} strokeWidth={3} />
                <Badge n={casellaUnread} id="caselle" />
              </button>
              {openMenu === "caselle" && (
                <div className="dropdown caselle-popup" data-title="Caselle" data-testid="dropdown-caselle">
                  {accTutti.map((a) => (
                    <div className="caselle-sez caselle-account caselle-tutti" key={a.id}>
                      <button
                        className={`chip chip-account dd-item ${accountFilter === "tutti" && categoria !== "social" ? "active" : ""}`}
                        onClick={() => { setAccountFilter("tutti"); setCategoria("tutti"); setSocialFilter(null); setOpenMenu(null); }}
                        data-testid="account-tutti"
                      >
                        {a.label}
                        <Badge n={unread.tutti} id="account-tutti" />
                      </button>
                    </div>
                  ))}

                  <div className="caselle-sez caselle-pec">
                    <div className="caselle-sez-title">PEC <Badge n={unread.pec} id="pec" /></div>
                    {pecList.length === 0 && (
                      <button className="chip dd-item" onClick={() => { onAddAccount(); setOpenMenu(null); }} data-testid="pec-empty-add">
                        + Aggiungi PEC
                      </button>
                    )}
                    {pecList.map((p) => (
                      <button
                        key={p.id}
                        className={`chip chip-pec dd-item ${accountFilter === p.id ? "active" : ""}`}
                        onClick={() => { setAccountFilter(accountFilter === p.id ? "tutti" : p.id); setCategoria("tutti"); setSocialFilter(null); setOpenMenu(null); }}
                        data-testid={`pec-${p.id}`}
                      >
                        <StatoDot id={p.id} />
                        {p.label}
                        <Badge n={unread.byAccount?.[p.id]} id={`pec-${p.id}`} />
                      </button>
                    ))}
                  </div>

                  <div className="caselle-sez caselle-account">
                    <div className="caselle-sez-title">ACCOUNT <Badge n={unread.tutti} id="account" /></div>
                    {accAltri.map((a) => (
                      <button
                        key={a.id}
                        className={`chip chip-account dd-item ${accountFilter === a.id && categoria !== "social" ? "active" : ""}`}
                        onClick={() => { setAccountFilter(a.id); setCategoria("tutti"); setSocialFilter(null); setOpenMenu(null); }}
                        title={a.email || undefined}
                        data-email={a.email || undefined}
                        data-testid={`account-${a.id}`}
                      >
                        <StatoDot id={a.id} />
                        {a.label}
                        <Badge n={unread.byAccount?.[a.id]} id={`account-${a.id}`} />
                      </button>
                    ))}
                    <button className="chip dd-item" onClick={() => { onAddAccount(); setOpenMenu(null); }} data-testid="account-empty-add">
                      + Aggiungi account
                    </button>
                  </div>

                  <div className="caselle-sez caselle-social">
                    <div className="caselle-sez-title">SOCIAL <Badge n={unread.social} id="social" /></div>
                    {(socialAccounts || []).map((a) => (
                      <button
                        key={a.id}
                        className={`chip chip-social dd-item ${accountFilter === a.id ? "active" : ""}`}
                        onClick={() => { setCategoria("social"); setSocialFilter(null); setAccountFilter(accountFilter === a.id ? "tutti" : a.id); setOpenMenu(null); }}
                        title={a.page_name ? `Pagina Facebook: ${a.page_name}` : a.email}
                        data-testid={`social-account-${a.id}`}
                      >
                        <StatoDot id={a.id} />
                        <Facebook size={15} strokeWidth={2.4} />
                        {a.label}
                        <Badge n={unread.byAccount?.[a.id]} id={`social-account-${a.id}`} />
                      </button>
                    ))}
                    <div className="social-grid-inline">
                      <button
                        className={`social-dot ${categoria === "social" && !socialFilter ? "social-active" : ""}`}
                        aria-label="Tutti i social"
                        title="Tutti i social"
                        onClick={() => { setCategoria("social"); setSocialFilter(null); setAccountFilter("tutti"); setOpenMenu(null); }}
                        data-testid="social-tutti"
                      >
                        <Layers size={20} strokeWidth={1.7} />
                      </button>
                      {SOCIALS.map((s) => (
                        <button
                          key={s.id}
                          className={`social-dot ${socialFilter === s.id ? "social-active" : ""}`}
                          aria-label={s.id}
                          title={s.id}
                          onClick={() => { setCategoria("social"); setSocialFilter(socialFilter === s.id ? null : s.id); setAccountFilter("tutti"); setOpenMenu(null); }}
                          style={{ color: s.color }}
                          data-testid={`social-${s.id}`}
                        >
                          <s.icon size={20} strokeWidth={1.7} />
                          <Badge n={unread.bySocial?.[s.id]} id={`social-${s.id}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Solo non lette */}
          <button
            className={`icon-plain unread-toggle con-tip ${soloNonLette ? "on" : ""}`}
            onClick={() => { onToggleNonLette(); setOpenMenu(null); }}
            aria-label={soloNonLette ? "Mostra tutte le email" : "Mostra solo non lette"}
            data-tip={soloNonLette ? "Mostra tutte" : "Solo non lette"}
            data-testid="unread-toggle"
          >
            {soloNonLette ? <MailOpen size={25} strokeWidth={2.6} /> : <Mail size={25} strokeWidth={2.6} />}
          </button>

          {/* Ingranaggio -> tendina strumenti */}
          <div className="anchor-wrap">
            <button
              className="icon-plain con-tip"
              onClick={() => toggle("utils")}
              aria-label="Impostazioni"
              data-tip="Impostazioni"
              data-testid="settings-btn"
            >
              <Settings size={26} strokeWidth={2.2} />
            </button>
            {openMenu === "utils" && (
              <div className="dropdown dashed align-right utils-grid" data-title="Impostazioni" data-testid="dropdown-utils">
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Aggiungi account" aria-label="Aggiungi account" data-testid="util-add-account" onClick={() => { onAddAccount(); setOpenMenu(null); }}>
                  <UserPlus size={24} strokeWidth={2.3} />
                </button>
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Gestisci account" aria-label="Gestisci account" data-testid="util-delete-account" onClick={() => { onDeleteAccountOpen(); setOpenMenu(null); }}>
                  <UserMinus size={24} strokeWidth={2.3} />
                </button>
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Firme" aria-label="Firme" data-testid="util-signatures" onClick={() => { onSignatures(); setOpenMenu(null); }}>
                  <PenLine size={24} strokeWidth={2.3} />
                </button>
                <button
                  className={`icon-plain dd-icon ${socialInArrivo ? "dd-icon-on" : ""}`}
                  title="Email Social"
                  aria-label="Email Social"
                  data-testid="util-social-inbox"
                  onClick={() => { onToggleSocialInArrivo?.(); setOpenMenu(null); }}
                >
                  <Share2 size={24} strokeWidth={2.3} />
                </button>
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Giorno / Notte" aria-label="Giorno / Notte" data-testid="dark-toggle" onClick={() => { toggleDark(); setOpenMenu(null); }}>
                  <SunMoon size={24} strokeWidth={2.3} />
                </button>
                <button
                  className={`icon-plain dd-icon ${notifiche === "attive" ? "notif-on" : ""}`}
                  title={notifiche === "attive" ? "Notifiche ON" : "Notifiche"}
                  aria-label="Notifiche"
                  data-testid="util-notifiche"
                  onClick={() => { onToggleNotifiche(); setOpenMenu(null); }}
                >
                  {notifiche === "attive" ? <Bell size={24} strokeWidth={2.3} fill="currentColor" /> : <BellOff size={24} strokeWidth={2.3} />}
                </button>
                {isAdmin && (
                  <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Manutenzione" aria-label="Manutenzione" data-testid="util-manutenzione" onClick={() => { onManutenzione(); setOpenMenu(null); }}>
                    <Wrench size={24} strokeWidth={2.3} />
                  </button>
                )}
                {isAdmin && (
                  <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Errori" aria-label="Errori" data-testid="util-diagnostica" onClick={() => { onDiagnostica(); setOpenMenu(null); }}>
                    <Activity size={24} strokeWidth={2.3} />
                  </button>
                )}
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="SMTP alternativo" aria-label="SMTP alternativo" data-testid="util-relay" onClick={() => { onRelay(); setOpenMenu(null); }}>
                  <Send size={24} strokeWidth={2.3} />
                </button>
                <button className={`icon-plain dd-icon con-tip tip-sx ${installabile ? "install-ready" : ""}`} data-tip="Installa app" aria-label="Installa app" data-testid="util-install" onClick={() => { onInstall(); setOpenMenu(null); }}>
                  <Download size={24} strokeWidth={2.3} />
                </button>
                {isAdmin && (
                  <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Utenti" aria-label="Utenti" data-testid="util-admin" onClick={() => { onAdmin(); setOpenMenu(null); }}>
                    <ShieldCheck size={24} strokeWidth={2.3} />
                  </button>
                )}
                <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Esci" aria-label="Esci" data-testid="util-logout" onClick={() => { onLogout(); setOpenMenu(null); }}>
                  <LogOut size={24} strokeWidth={2.3} />
                </button>
                {backupAllowed && (
                  <button className="icon-plain dd-icon con-tip tip-sx" data-tip="Scarica backup" aria-label="Scarica backup" data-testid="util-backup" onClick={() => { onBackup(); setOpenMenu(null); }}>
                    <HardDriveDownload size={24} strokeWidth={2.3} />
                  </button>
                )}
              </div>
            )}
          </div>

          <button className="icon-plain con-tip" onClick={() => { setSearchOpen(true); setOpenMenu(null); }} aria-label="Cerca" data-tip="Cerca" data-testid="search-btn">
            <Search size={26} strokeWidth={2.6} />
          </button>
          <button className="icon-btn compose-btn con-tip" onClick={onCompose} data-testid="compose-btn" aria-label="Scrivi" data-tip="Scrivi">
            <SquarePen size={28} strokeWidth={2.5} />
          </button>
        </>
      )}
    </div>
  );
}
